import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import TransactionDetailModal from "./TransactionDetailModal";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { formatAmount, formatDate } from "../../utils/utils";

const TYPES = ["all", "cart", "booking"];

const chipSx = (val) =>
  ({
    cart: { bgcolor: "#dbeafe", color: "#1d4ed8" },
    booking: { bgcolor: "#ede9fe", color: "#5b21b6" },
    paid: { bgcolor: "#d1fae5", color: "#065f46" },
    pending: { bgcolor: "#fef3c7", color: "#92400e" },
    failed: { bgcolor: "#fee2e2", color: "#991b1b" },
  }[val] || { bgcolor: "#f3f4f6", color: "#374151" });

export default function TransactionsTable() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const uid = user?.uid;
  const isAdmin = user?.role === "admin";

  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const PER_PAGE = 10;

  useEffect(() => {
    if (!uid) return;
    (async () => {
      try {
        let snap;
        if (isAdmin) {
          snap = await getDocs(
            query(collection(db, "transactions"), orderBy("createdAt", "desc"))
          );
        } else {
          snap = await getDocs(
            query(collection(db, "transactions"), where("userId", "==", uid))
          );
        }
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // sort newest first in JS (avoids needing a composite Firestore index)
        docs.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
        setAllDocs(docs);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid, isAdmin]);

  const filtered =
    typeFilter === "all" ? allDocs : allDocs.filter((d) => d.type === typeFilter);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
          {filtered.length} transaction{filtered.length !== 1 ? "s" : ""}
        </Typography>
        <Select
          value={typeFilter}
          onChange={handleTypeChange}
          size="small"
          sx={{ fontSize: 13, minWidth: 130, bgcolor: "#fff", borderRadius: "8px" }}
        >
          {TYPES.map((t) => (
            <MenuItem key={t} value={t} sx={{ fontSize: 13, textTransform: "capitalize" }}>
              {t === "all" ? "All Types" : t.charAt(0).toUpperCase() + t.slice(1)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {paged.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>
              No transactions found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                  {(isAdmin ? ["Reference", "User", "Amount", "Type", "Status", "Date", ""] : ["Reference", "Amount", "Type", "Status", "Date", ""]).map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 16px",
                        textAlign: "left",
                        fontSize: 11,
                        color: "#6b7280",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((tx) => {
                  const date =
                    tx.createdAt?.toDate?.() ||
                    (tx.createdAt?.seconds ? new Date(tx.createdAt.seconds * 1000) : null) ||
                    (tx.purchase_time ? new Date(tx.purchase_time) : null);
                  return (
                    <tr
                      key={tx.id}
                      style={{ borderBottom: "1px solid #f3f4f6" }}
                    >
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#374151", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {tx.paymentIntentId || "—"}
                      </td>
                      {isAdmin && (
                        <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>
                          {tx.userId?.slice(0, 12)}…
                        </td>
                      )}
                      <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {formatAmount(tx.amount)}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <Chip
                          label={tx.type || "cart"}
                          size="small"
                          sx={{ ...chipSx(tx.type), fontSize: 11, height: 22, textTransform: "capitalize" }}
                        />
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <Chip
                          label={tx.status || "pending"}
                          size="small"
                          sx={{ ...chipSx(tx.status), fontSize: 11, height: 22, textTransform: "capitalize" }}
                        />
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                        {date ? formatDate(date) : "—"}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <Box
                          onClick={() => setSelected(tx)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1.5,
                            py: 0.5,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#194049",
                            border: "1px solid #194049",
                            borderRadius: "6px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            "&:hover": { bgcolor: "#194049", color: "#fff" },
                            transition: "all 0.15s",
                          }}
                        >
                          View
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}

        {/* Pagination — always visible */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
            Page {page + 1} of {Math.max(totalPages, 1)}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {["Prev", "Next"].map((label) => (
              <Box
                key={label}
                onClick={() => setPage((p) => label === "Prev" ? Math.max(0, p - 1) : Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: 2,
                  py: 0.75,
                  fontSize: 12,
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  cursor: "pointer",
                  bgcolor: "#fff",
                  color: "#374151",
                  "&:hover": { bgcolor: "#f9fafb" },
                  opacity: (label === "Prev" && page === 0) || (label === "Next" && page >= totalPages - 1) ? 0.4 : 1,
                  pointerEvents: (label === "Prev" && page === 0) || (label === "Next" && page >= totalPages - 1) ? "none" : "auto",
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {selected && (
        <TransactionDetailModal transaction={selected} onClose={() => setSelected(null)} />
      )}
    </Box>
  );
}
